<template>
  <section class="gamanza-widget-page" :style="pageStyle">
    <div v-if="!gamanzaReady" class="gamanza-widget-page__loading">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
    </div>
    <div v-else :id="widgetId" class="gamification_widget" :data-type="type"></div>
  </section>
</template>

<script>
import gamanzaWidgetService from '@/services/gamanzaWidgetService';

export default {
  name: 'GamanzaWidgetPage',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['missions', 'tournaments', 'reward-shop'].includes(value)
    },
    skin: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      widgetId: `gamanza-${this.type}-page-${this._uid}`,
      gameClickHandler: null
    };
  },
  computed: {
    gamanzaReady() {
      return this.$store.getters['getGamanzaReady'];
    },
    pageStyle() {
      const palette = this.skin?.gamanza?.lookAndFeel?.palette;

      return {
        '--gamanza-page-background': palette?.background?.default || '#040713',
        '--gamanza-page-accent': palette?.primary?.main || '#DA4167'
      };
    }
  },
  watch: {
    gamanzaReady(isReady) {
      if (isReady) {
        this.reloadWidget();
      }
    }
  },
  mounted() {
    this.gameClickHandler = (event) => this.onGameClick(event);
    document.addEventListener('click', this.gameClickHandler, true);

    if (this.gamanzaReady) {
      this.reloadWidget();
    }
  },
  beforeDestroy() {
    if (this.gameClickHandler) {
      document.removeEventListener('click', this.gameClickHandler, true);
    }
  },
  methods: {
    reloadWidget() {
      this.$nextTick(() => {
        setTimeout(() => gamanzaWidgetService.reloadOne(this.widgetId), 300);
      });
    },
    onGameClick(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const page = document.getElementById(this.widgetId);
      const gameLink = target.closest('a.GamanzaTypography-root');
      const gameCard = target.closest('.GamanzaGrid-root');
      if ((!gameLink || !gameLink.closest('.GamanzaGrid-root')) && !gameCard) return;

      const isInsideWidget = Boolean(page?.contains(target));
      const gamanzaDialog = target.closest('.GamanzaDialog-root, .GamanzaModal-root');
      const isPortalGameDialog = Boolean(
        ['missions', 'tournaments'].includes(this.type) &&
        gamanzaDialog?.querySelector('.GamanzaGrid-root')
      );

      console.log('[GamanzaWidgetPage] game card click detected', {
        type: this.type,
        target,
        isInsideWidget,
        isPortalGameDialog,
        gameLink,
        gameCard
      });

      // Gamanza mounts game-selection dialogs in a portal outside the widget page.
      // Accept those game cards as well, while keeping unrelated document clicks ignored.
      if (!isInsideWidget && !isPortalGameDialog) {
        console.log('[GamanzaWidgetPage] game card click ignored: outside widget and portal dialog');
        return;
      }

      const gameName = gameLink?.querySelector(':scope > p')?.textContent?.trim()
        || gameLink?.querySelector(':scope > img')?.alt?.trim()
        || gameCard?.querySelector(':scope > img[alt]')?.alt?.trim();
      if (!gameName) {
        console.log('[GamanzaWidgetPage] game card click ignored: game name not found');
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.closeGameDialog();
      console.log('[GamanzaWidgetPage] emitting selected game', { type: this.type, gameName });
      this.$root.$emit('gamanza-game-selected', gameName);
    },
    closeGameDialog() {
      const dialog = Array.from(document.querySelectorAll('.GamanzaDialog-root, .GamanzaModal-root'))
        .find((element) => element.querySelector('.GamanzaGrid-root'));
      const closeButton = dialog?.querySelector('[aria-label="Close"], button[class*="close"], button[class*="Close"]');

      closeButton?.click();
    }
  }
};
</script>

<style scoped>
.gamanza-widget-page {
  position: relative;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
  isolation: isolate;
  background: var(--gamanza-page-background);
}

.gamanza-widget-page::before {
  position: absolute;
  z-index: 0;
  top: 62px;
  left: 0;
  width: 100%;
  height: min(76vw, 860px);
  content: '';
  background:
    radial-gradient(ellipse 36% 34% at 4% 27%, var(--gamanza-page-accent) 0%, transparent 100%),
    radial-gradient(ellipse 36% 34% at 96% 48%, var(--gamanza-page-accent) 0%, transparent 100%);
  filter: blur(38px);
  opacity: 0.25;
  pointer-events: none;
  transform: translate3d(0, 0, 0) scale(1);
  animation: gamanza-page-glow 18s ease-in-out infinite alternate;
}

.gamanza-widget-page::after {
  position: absolute;
  z-index: 0;
  inset: 0;
  content: '';
  background-image:
    linear-gradient(to bottom, var(--gamanza-page-accent), transparent 310px),
    linear-gradient(rgba(255, 255, 255, 0.028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.028) 1px, transparent 1px);
  background-size: 34px 34px;
  -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.72), transparent 460px);
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.72), transparent 460px);
  opacity: 0.16;
  pointer-events: none;
}

.gamanza-widget-page .gamification_widget,
.gamanza-widget-page__loading {
  position: relative;
  z-index: 1;
}

.gamanza-widget-page__loading {
  display: grid;
  min-height: 320px;
  place-items: center;
  color: #fff;
}

@keyframes gamanza-page-glow {
  from {
    transform: translate3d(-1%, -8px, 0) scale(1);
  }

  to {
    transform: translate3d(1%, 18px, 0) scale(1.05);
  }
}

@media (prefers-reduced-motion: reduce) {
  .gamanza-widget-page::before {
    animation: none;
  }
}

</style>
